// "Edit with AI" in the page editor's AI tab. The AI only ever sees and
// changes words: each section goes over as its id, type and text, and links,
// images, videos, embeds, forms and styles never leave the editor. Its answer
// is a list of changes (updates, added sections, removed sections) that is
// checked field by field here and applied as one edit, so Undo brings the
// page back. The request itself goes through the staff API (aiEditSections in
// ../api.ts, POST /api/pages/ai-edit); this file holds only the pure parts.
import { SECTION_TYPES, newSectionFilled, type Section, type SectionType } from './pages';

/** Plain text fields the AI may rewrite. */
export const AI_TEXT_FIELDS = [
  'eyebrow', 'headline', 'subheadline', 'heading', 'body', 'label', 'cta_label', 'cta2_label',
  'quote', 'attribution', 'role', 'alt', 'left_title', 'right_title', 'expired_text',
  'highlight', 'proof_text', 'cta_note', 'price', 'price_compare', 'price_note',
] as const;
/** Lists of plain text the AI may rewrite. */
export const AI_TEXT_LISTS = ['items', 'left_items', 'right_items'] as const;
/** Lists of small records: the text parts of each item the AI may rewrite.
 *  Everything else on an item (photos, icons, links, ratings) is kept. */
export const AI_ITEM_LISTS: Record<string, readonly string[]> = {
  stats: ['value', 'label'],
  features: ['title', 'body'],
  faqs: ['q', 'a'],
  steps: ['label', 'detail'],
  quotes: ['quote', 'name', 'role'],
  modules: ['label', 'title', 'body', 'lessons'],
  offer_items: ['label', 'value'],
  plans: ['name', 'price', 'period', 'features', 'cta_label', 'description', 'price_compare', 'badge'],
  members: ['name', 'role'],
};
/** Item text parts that are lists of strings themselves. */
const ITEM_STRING_LISTS = new Set(['lessons', 'features']);
/** Section types the AI may add (all of them work with words alone). */
export const AI_ADDABLE: readonly SectionType[] = [
  'text', 'benefits', 'compare', 'features', 'stats', 'steps', 'faq', 'testimonials', 'cta', 'guarantee', 'offer', 'modules',
];

const MAX_TEXT = 2_000;
const MAX_ITEMS = 30;
const KNOWN_TYPES = new Set<string>(SECTION_TYPES);

/** A section as the AI sees it: id, type and words only. */
export type AiSection = { id: string; type: SectionType } & Record<string, unknown>;

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' ? v.slice(0, MAX_TEXT) : null);
const strList = (v: unknown): string[] | null =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, MAX_ITEMS).map((x) => x.slice(0, MAX_TEXT)) : null;

/** The words of each section, for the AI. Pure. */
export function projectForAi(sections: Section[]): AiSection[] {
  return sections.map((sec) => {
    const src = sec as unknown as Record<string, unknown>;
    const out: AiSection = { id: sec.id, type: sec.type };
    for (const f of AI_TEXT_FIELDS) {
      const v = src[f];
      if (typeof v === 'string' && v.trim()) out[f] = v;
    }
    for (const f of AI_TEXT_LISTS) {
      const v = strList(src[f]);
      if (v && v.length) out[f] = v;
    }
    for (const [f, parts] of Object.entries(AI_ITEM_LISTS)) {
      const list = src[f];
      if (!Array.isArray(list) || !list.length) continue;
      out[f] = list.slice(0, MAX_ITEMS).map((item) => {
        const it: Record<string, unknown> = {};
        if (isObj(item)) for (const p of parts) {
          const v = ITEM_STRING_LISTS.has(p) ? strList(item[p]) : str(item[p]);
          if (v !== null && (Array.isArray(v) ? v.length : v.trim())) it[p] = v;
        }
        return it;
      });
    }
    return out;
  });
}

/** Copies the words the AI sent into `base`, field by field, keeping
 *  everything that is not words. Returns the new section. Pure. */
function withWords(base: Section, words: Record<string, unknown>): Section {
  const next: Record<string, unknown> = { ...base };
  for (const f of AI_TEXT_FIELDS) {
    if (!(f in words)) continue;
    const v = str(words[f]);
    if (v === null) continue;
    if (v.trim()) next[f] = v.trim();
    else delete next[f];
  }
  for (const f of AI_TEXT_LISTS) {
    if (!(f in words)) continue;
    const v = strList(words[f]);
    if (v) next[f] = v;
  }
  for (const [f, parts] of Object.entries(AI_ITEM_LISTS)) {
    if (!(f in words) || !Array.isArray(words[f])) continue;
    const before = Array.isArray(next[f]) ? (next[f] as unknown[]) : [];
    next[f] = (words[f] as unknown[]).filter(isObj).slice(0, MAX_ITEMS).map((item, i) => {
      // An item keeps what is not words (a photo, an icon, a link) from the
      // item that sat in the same place before.
      const old = isObj(before[i]) ? before[i] as Record<string, unknown> : {};
      const it: Record<string, unknown> = { ...old };
      for (const p of parts) {
        if (!(p in item)) continue;
        const v = ITEM_STRING_LISTS.has(p) ? strList(item[p]) : str(item[p]);
        if (v !== null) it[p] = v;
      }
      return it;
    });
  }
  // A highlight has to be words of the headline (or heading); if the new text
  // no longer has them, the highlight goes.
  const hl = typeof next.highlight === 'string' ? next.highlight : '';
  const title = String(next.headline ?? next.heading ?? '');
  if (hl && !title.includes(hl)) delete next.highlight;
  return next as unknown as Section;
}

/** What the AI's answer did to the sections. */
export interface AiApplied { sections: Section[]; changed: number; added: number; removed: number }

/** Applies the AI's answer ({ updates, added, removed }) to the sections:
 *  updates only touch words of sections that exist, added sections are of
 *  word-only types, removals only take word-only sections that exist (never
 *  the form, a booking calendar, the nav, a hero or any other built piece) and
 *  never all of them. Anything malformed is skipped. Pure. */
export function applyAiChanges(original: Section[], result: unknown, mkId: () => string): AiApplied {
  const r = isObj(result) ? result : {};
  let sections = [...original];
  let changed = 0;
  for (const u of Array.isArray(r.updates) ? r.updates : []) {
    if (!isObj(u) || typeof u.id !== 'string') continue;
    const i = sections.findIndex((s) => s.id === u.id);
    if (i < 0) continue;
    const next = withWords(sections[i], u);
    if (JSON.stringify(next) !== JSON.stringify(sections[i])) { sections[i] = next; changed++; }
  }
  let removed = 0;
  const drop = new Set((Array.isArray(r.removed) ? r.removed : []).filter((x): x is string => typeof x === 'string'));
  const kept = sections.filter((s) => !drop.has(s.id) || !AI_ADDABLE.includes(s.type));
  if (kept.length && kept.length < sections.length) { removed = sections.length - kept.length; sections = kept; }
  let added = 0;
  for (const a of Array.isArray(r.added) ? r.added : []) {
    if (!isObj(a) || typeof a.type !== 'string' || !KNOWN_TYPES.has(a.type)) continue;
    const type = a.type as SectionType;
    if (!AI_ADDABLE.includes(type)) continue;
    const sec = withWords(newSectionFilled(type, mkId()), a);
    const at = typeof a.after === 'string' ? sections.findIndex((s) => s.id === a.after) : -1;
    if (a.after === null) sections = [sec, ...sections];
    else if (at >= 0) sections = [...sections.slice(0, at + 1), sec, ...sections.slice(at + 1)];
    else sections = [...sections, sec];
    added++;
  }
  return { sections, changed, added, removed };
}

/** The AI's answer, before it is checked. */
export interface AiEditResult { result: unknown; summary: string }
